import React, { FC, useState, useRef, useEffect, useMemo } from 'react';
import { auth } from 'src/config/firebase';
import { useProgramGeneratorProgressStore } from 'src/store/programGeneratorProgressStore';
import { sessionStore } from 'src/store/sessionStore';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { Modal, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useGetClientProgramTab } from 'src/api/queries/useGetClientProgramTab';
import { EmptyState } from './EmptyState';
import { useGetProgramMasterData } from 'src/api/mutations/useGetProgramMasterData';
import { useUpdateProgramSchedule } from 'src/api/mutations/useUpdateProgramSchedule';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { ReactComponent as Intensity_setupIcon } from '../../ProgramGeneratorAI/../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/intensity_setup.svg';
import { ReactComponent as Program_goalsIcon } from '../../ProgramGeneratorAI/../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/program_goals.svg';
import { ReactComponent as Goal_approachIcon } from '../../ProgramGeneratorAI/../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/goal_approach.svg';
import { ReactComponent as Plan_structureIcon } from '../../ProgramGeneratorAI/../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/plan_structure.svg';
import { ReactComponent as EditIcon } from '../../ProgramGeneratorAI/../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/edit.svg';

import { ReactComponent as ProgramWeightIcon } from '../../ProgramGeneratorAI/../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/weight.svg';
import { EquipmentIcon } from 'src/utils/equipmentIcons';
import { isFemaleProfile, isMaleProfile } from 'src/utils/genderUtils';
import { getIntensityActiveDots } from './ProgramTab/IntensityCard';
import { EquipmentsModal } from './ProgramTab/EquipmentsModal';

import ProgramFemaleImg from 'url:../../ProgramGeneratorAI/assets/client/program-female.png';
import ProgramMaleImg from 'url:../../ProgramGeneratorAI/assets/client/program-male.png';
import { ReactComponent as PerformanceAdherenceIcon } from 'src/assets/images/trainer/clients/program_workout_performance_adherence.svg';

import { ReactComponent as ProgramWorkoutIcon } from 'src/assets/images/trainer/clients/program_workout.svg';
import { ReactComponent as PlanCalendarIcon } from 'src/assets/images/trainer/clients/overview-overview-program-overview.svg';
import { ReactComponent as CalendarIcon } from 'src/assets/images/trainer/clients/overview-header-calendar.svg';
import { ReactComponent as MoreProgramWorkoutIcon } from 'src/assets/images/trainer/clients/more_program_workout.svg';

import { ReactComponent as NavArrowIcon } from 'src/assets/images/trainer/clients/nav-arrow-icon.svg';
import { ReactComponent as NotificationIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/notification.svg';
import { ReactComponent as OnboardModalInfoIcon } from 'src/assets/images/trainer/clients/onboard-modal-info.svg';

interface ProgramTabProps {
  client?: any;
}

const getLocalYMD = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dayNum = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dayNum}`;
};

export const ProgramTab: FC<ProgramTabProps> = () => {
  const { client } = useOutletContext<{ client: any }>();
  const clientId = client?.uid;
  const clientName = client ? `${client.name || ''} ${client.surname || ''}`.trim() : undefined;
  const currentUser = sessionStore((state) => state.currentUser);
  const trainerUid = auth.currentUser?.uid || currentUser?.uid || '';
  const { data: programData, isLoading } = useGetClientProgramTab(clientId, trainerUid);
  const { data: masterData } = useGetProgramMasterData();
  const queryClient = useQueryClient();
  const updateProgramSchedule = useUpdateProgramSchedule();

  const getSlugTitle = (slug: string): string => {
    if (!slug) return '';
    let result: string | null = null;
    if (masterData) {
      const search = (obj: any) => {
        if (result) return;
        if (!obj || typeof obj !== 'object') return;
        if ((obj.slug === slug || obj.id === slug) && obj.title) {
          result = obj.title;
          return;
        }
        if (Array.isArray(obj)) {
          obj.forEach(search);
        } else {
          Object.values(obj).forEach(search);
        }
      };
      search(masterData);
    }

    if (result) return result;

    return String(slug)
      .replace(/_/g, '-')
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const weeksData = programData?.weeksData || [];
  const [activeWeek, setActiveWeek] = useState(weeksData.length > 0 ? weeksData[0].week : 1);
  const [activeDayIndex, setActiveDayIndex] = useState(0);
  const [isEquipmentsModalOpen, setIsEquipmentsModalOpen] = useState(false);

  const minWeek = weeksData.length > 0 ? weeksData[0].week : 1;
  const maxWeek = weeksData.length > 0 ? weeksData[weeksData.length - 1].week : 1;
  const isFirstWeek = activeWeek <= minWeek;
  const isLastWeek = activeWeek >= maxWeek;

  const handlePrevWeekTab = () => {
    if (!isFirstWeek) {
      setActiveWeek((prev: number) => Math.max(minWeek, prev - 1));
    }
  };

  const handleNextWeekTab = () => {
    if (!isLastWeek) {
      setActiveWeek((prev: number) => Math.min(maxWeek, prev + 1));
    }
  };

  useEffect(() => {
    setActiveDayIndex(0);
    if (weeksListRef.current) {
      const activeEl = weeksListRef.current.querySelector('.active') as HTMLElement;
      if (activeEl) {
        const container = weeksListRef.current;
        const scrollLeft =
          activeEl.offsetLeft - container.clientWidth / 2 + activeEl.clientWidth / 2;
        container.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
      }
    }
  }, [activeWeek]);

  const activeWeekData = weeksData.find((w: any) => w.week === activeWeek) || { days: [] };
  const exercisesToRender = activeWeekData.days[activeDayIndex]?.exercises || [];

  const getSelectedWorkoutId = (): number => {
    const activeDay = activeWeekData?.days?.[activeDayIndex];
    if (activeDay?.dateYmd && programData?.datetimeList) {
      const rawDt = [...(programData.datetimeList || [])]
        .filter((dt: any) => dt?.ymd)
        .sort((a: any, b: any) => (a.ymd || '').localeCompare(b.ymd || ''));
      const foundIdx = rawDt.findIndex((dt: any) => dt.ymd === activeDay.dateYmd);
      if (foundIdx !== -1) {
        return foundIdx + 1;
      }
    }
    let globalWorkoutIndex = 0;
    for (const w of weeksData) {
      if (w.week === activeWeek) {
        globalWorkoutIndex += activeDayIndex + 1;
        return globalWorkoutIndex;
      }
      globalWorkoutIndex += w.days?.length || 0;
    }
    return 1;
  };

  const [visibleThumbnails, setVisibleThumbnails] = useState(7);
  const weeksListRef = useRef<HTMLDivElement>(null);
  const thumbnailsContainerRef = useRef<HTMLDivElement>(null);
  const [isPlanCalendarModalOpen, setIsPlanCalendarModalOpen] = useState(false);
  const navigate = useNavigate();
  const [modalTab, setModalTab] = useState<'permanent' | 'temporary'>('permanent');
  const [modalDate, setModalDate] = useState(new Date());

  const [currentDate, setCurrentDate] = useState(new Date());
  const [draftDatetimeList, setDraftDatetimeList] = useState<any[]>([]);
  const [selectedSource, setSelectedSource] = useState<string | number | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const chartContentRef = useRef<HTMLDivElement>(null);
  const isDraggingChartRef = useRef(false);
  const chartStartXRef = useRef(0);
  const chartScrollLeftRef = useRef(0);

  const [chartScrollInfo, setChartScrollInfo] = useState<{
    scrollProgress: number;
    thumbWidth: number;
    isScrollable: boolean;
    canScrollLeft: boolean;
    canScrollRight: boolean;
  }>({
    scrollProgress: 0,
    thumbWidth: 30,
    isScrollable: false,
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateChartScroll = () => {
    if (chartContentRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = chartContentRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const isScrollable = maxScroll > 2;
      const thumbRatio = Math.max(0.15, Math.min(1, clientWidth / scrollWidth));
      const thumbWidthPercent = thumbRatio * 100;
      const scrollPercent = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      setChartScrollInfo({
        scrollProgress: scrollPercent,
        thumbWidth: thumbWidthPercent,
        isScrollable,
        canScrollLeft: scrollLeft > 5,
        canScrollRight: scrollLeft < maxScroll - 5,
      });
    }
  };

  const handleScrollChart = (direction: 'left' | 'right') => {
    if (chartContentRef.current) {
      const scrollAmount = Math.max(260, chartContentRef.current.clientWidth * 0.6);
      chartContentRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const handleChartMouseDown = (e: React.MouseEvent) => {
    if (!chartContentRef.current) return;
    isDraggingChartRef.current = true;
    chartStartXRef.current = e.pageX - chartContentRef.current.offsetLeft;
    chartScrollLeftRef.current = chartContentRef.current.scrollLeft;
  };

  const handleChartMouseMove = (e: React.MouseEvent) => {
    if (!isDraggingChartRef.current || !chartContentRef.current) return;
    e.preventDefault();
    const x = e.pageX - chartContentRef.current.offsetLeft;
    const walk = (x - chartStartXRef.current) * 1.5;
    chartContentRef.current.scrollLeft = chartScrollLeftRef.current - walk;
  };

  const handleChartMouseUpOrLeave = () => {
    isDraggingChartRef.current = false;
  };

  useEffect(() => {
    updateChartScroll();
    const handleResize = () => updateChartScroll();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [programData]);

  useEffect(() => {
    if (isPlanCalendarModalOpen && programData?.datetimeList) {
      const dtList = JSON.parse(JSON.stringify(programData.datetimeList));
      setDraftDatetimeList(dtList);
      setModalDate(new Date());
      setSelectedSource(null);
      setInlineError(null);
      setModalTab('permanent');
    }
  }, [isPlanCalendarModalOpen, programData]);

  const handleEditProgram = (stepSlug?: string, subStepSlug?: string) => {
    if (!programData) return;
    const store = useProgramGeneratorProgressStore.getState();
    const pid = (programData as any).programId || (programData as any).id || '';
    store.resetProgress();
    store.setClientId(clientId);
    if (pid) {
      store.setProgramId(pid);
    }
    if (programData.progress) {
      store.setProgress(programData.progress);
    }
    const queryParams = new URLSearchParams({ clientId });
    if (pid) {
      queryParams.set('programId', pid);
    }
    if (stepSlug) {
      queryParams.set('step', stepSlug);
    }
    if (subStepSlug) {
      queryParams.set('subStep', subStepSlug);
    }
    const goal =
      programData.progress?.training_goal?.[0]?.value ||
      (programData.primaryGoal
        ? String(programData.primaryGoal)
            .toLowerCase()
            .replace(/[\s_]+/g, '-')
        : '');
    if (goal) {
      queryParams.set('goal', goal);
    }
    queryParams.set('editMode', 'true');
    navigate(`/onboarding-trainer/dashboard/program-generator/ai?${queryParams.toString()}`);
  };

  const updateItemDateFields = (item: any, newYmd: string) => {
    const [y, m, d] = newYmd.split('-');
    const dObj = new Date(Number(y), Number(m) - 1, Number(d));
    const weekdayNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    const weekday2Names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const jsDay = dObj.getDay();

    return {
      ...item,
      ymd: newYmd,
      year: Number(y),
      month: Number(m),
      day: String(Number(d)),
      weekday: weekdayNames[jsDay],
      weekday2: weekday2Names[jsDay],
      weekdayNum: jsDay === 0 ? 7 : jsDay,
    };
  };

  const handleDayClick = (day: any) => {
    if (day.isPast || updateProgramSchedule.isPending) return;
    setInlineError(null);

    if (modalTab === 'temporary') {
      if (!selectedSource) {
        if (day.isWorkout) {
          setSelectedSource(day.ymd);
        }
      } else {
        if (day.ymd === selectedSource) {
          setSelectedSource(null);
        } else if (!day.isWorkout) {
          // Move workout
          const newList = [...draftDatetimeList];
          const itemIndex = newList.findIndex((item) => item.ymd === selectedSource);
          if (itemIndex !== -1) {
            newList[itemIndex] = updateItemDateFields(newList[itemIndex], day.ymd);
            setDraftDatetimeList(newList);
            setSelectedSource(null);
          }
        } else {
          setInlineError('Destination date already contains a workout');
        }
      }
    } else if (modalTab === 'permanent') {
      if (selectedSource === null) {
        if (day.isWorkout) {
          setSelectedSource(day.jsWeekday);
        }
      } else {
        if (day.jsWeekday === selectedSource) {
          setSelectedSource(null);
        } else {
          // Check if destination weekday already has workouts in the future
          const destHasWorkout = draftDatetimeList.some((item) => {
            if (item.ymd < getLocalYMD(new Date())) return false;
            const [y, m, d] = item.ymd.split('-');
            const dateObj = new Date(Number(y), Number(m) - 1, Number(d));
            return dateObj.getDay() === day.jsWeekday;
          });

          if (destHasWorkout) {
            setInlineError('Destination weekday already has a workout scheduled');
            return;
          }

          const sourceWeekday = selectedSource as number;
          const destWeekday = day.jsWeekday;
          const sourceIso = sourceWeekday === 0 ? 7 : sourceWeekday;
          const destIso = destWeekday === 0 ? 7 : destWeekday;
          const diff = destIso - sourceIso;
          const todayYmd = getLocalYMD(new Date());

          const newList = draftDatetimeList.map((item) => {
            if (item.ymd <= todayYmd) return item;
            const [y, m, d] = item.ymd.split('-');
            const dateObj = new Date(Number(y), Number(m) - 1, Number(d));

            if (dateObj.getDay() === sourceWeekday) {
              const targetDate = new Date(dateObj);
              targetDate.setDate(targetDate.getDate() + diff);
              if (getLocalYMD(targetDate) <= todayYmd) {
                targetDate.setDate(targetDate.getDate() + 7);
              }
              const newYmd = getLocalYMD(targetDate);
              // Only move if new date is strictly in the future and no collision
              if (newYmd > todayYmd && !draftDatetimeList.some((w) => w.ymd === newYmd)) {
                return updateItemDateFields(item, newYmd);
              }
            }
            return item;
          });
          setDraftDatetimeList(newList);
          setSelectedSource(null);
        }
      }
    }
  };

  const generateModalCalendarDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();

    let startDayOfWeek = firstDayOfMonth.getDay() - 1;
    if (startDayOfWeek === -1) startDayOfWeek = 6;

    const days = [];
    const today = new Date();
    const todayY = today.getFullYear();
    const todayM = today.getMonth();
    const todayD = today.getDate();
    const todayYmd = getLocalYMD(today);

    const prevMonthLastDay = new Date(year, month, 0).getDate();
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const d = prevMonthLastDay - i;
      days.push({
        date: d,
        isCurrentMonth: false,
        isToday: d === todayD && prevMonth === todayM && prevYear === todayY,
        month: prevMonth,
        year: prevYear,
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: i,
        isCurrentMonth: true,
        isToday: i === todayD && month === todayM && year === todayY,
        month: month,
        year: year,
      });
    }

    const totalDays = days.length;
    const remainingDays = 42 - totalDays;
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: i,
        isCurrentMonth: false,
        isToday: i === todayD && nextMonth === todayM && nextYear === todayY,
        month: nextMonth,
        year: nextYear,
      });
    }

    return days.map((day, index) => {
      const colIndex = index % 7;
      const jsWeekday = (colIndex + 1) % 7;

      const mStr = String(day.month + 1).padStart(2, '0');
      const dStr = String(day.date).padStart(2, '0');
      const ymd = `${day.year}-${mStr}-${dStr}`;
      const isPast = ymd <= todayYmd;
      const isWorkout = draftDatetimeList.some((item: any) => item.ymd === ymd);

      let isSelectedSource = false;
      if (modalTab === 'temporary') {
        isSelectedSource = selectedSource === ymd;
      } else if (modalTab === 'permanent') {
        isSelectedSource = selectedSource === jsWeekday && isWorkout;
      }

      return { ...day, isWorkout, ymd, jsWeekday, isPast, isSelectedSource };
    });
  };

  // Fixed frequency and days from original program plan
  const originalPlanDaysInfo = useMemo(() => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const howOften = programData?.progress?.how_often_train || programData?.progress?.howOftenTrain;
    if (Array.isArray(howOften) && howOften.length > 0) {
      const WEEKDAY_MAP: Record<string, { name: string; order: number }> = {
        sunday: { name: 'Sun', order: 7 },
        monday: { name: 'Mon', order: 1 },
        tuesday: { name: 'Tue', order: 2 },
        wednesday: { name: 'Wed', order: 3 },
        thursday: { name: 'Thu', order: 4 },
        friday: { name: 'Fri', order: 5 },
        saturday: { name: 'Sat', order: 6 },
      };
      const days = howOften
        .map((item: any) => {
          const key = String(item?.slug || item?.value || item || '')
            .trim()
            .toLowerCase();
          return WEEKDAY_MAP[key];
        })
        .filter(Boolean);
      if (days.length > 0) {
        days.sort((a, b) => a.order - b.order);
        return {
          daysString: days.map((d) => d.name).join(' / '),
          frequency: days.length,
        };
      }
    }

    if (
      programData?.planCalendar?.workoutDays &&
      Array.isArray(programData.planCalendar.workoutDays) &&
      programData.planCalendar.workoutDays.length > 0
    ) {
      const sorted = [...programData.planCalendar.workoutDays].sort((a: number, b: number) => {
        const aVal = a === 0 ? 7 : a;
        const bVal = b === 0 ? 7 : b;
        return aVal - bVal;
      });
      return {
        daysString: sorted.map((d: number) => dayNames[d]).join(' / '),
        frequency: sorted.length,
      };
    }

    if (
      programData?.datetimeList &&
      Array.isArray(programData.datetimeList) &&
      programData.datetimeList.length > 0
    ) {
      const daysSet = new Set<number>();
      programData.datetimeList.forEach((item: any) => {
        if (!item?.ymd) return;
        const [y, m, d] = item.ymd.split('-');
        daysSet.add(new Date(Number(y), Number(m) - 1, Number(d)).getDay());
      });
      const sorted = Array.from(daysSet).sort((a, b) => {
        const aVal = a === 0 ? 7 : a;
        const bVal = b === 0 ? 7 : b;
        return aVal - bVal;
      });
      return {
        daysString: sorted.map((d) => dayNames[d]).join(' / '),
        frequency: sorted.length,
      };
    }

    return {
      daysString: 'Mon / Wed / Thu / Fri',
      frequency: 4,
    };
  }, [programData]);

  const modalPlanDaysInfo = useMemo(() => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const todayYmd = getLocalYMD(new Date());

    if (modalTab === 'permanent' && draftDatetimeList && draftDatetimeList.length > 0) {
      const futureWorkouts = draftDatetimeList.filter(
        (item: any) => item?.ymd && item.ymd >= todayYmd,
      );
      const targetList = futureWorkouts.length > 0 ? futureWorkouts : draftDatetimeList;

      const daysSet = new Set<number>();
      targetList.forEach((item: any) => {
        if (!item?.ymd) return;
        const [y, m, d] = item.ymd.split('-');
        daysSet.add(new Date(Number(y), Number(m) - 1, Number(d)).getDay());
      });

      const sorted = Array.from(daysSet).sort((a, b) => {
        const aVal = a === 0 ? 7 : a;
        const bVal = b === 0 ? 7 : b;
        return aVal - bVal;
      });

      if (sorted.length > 0) {
        return {
          daysString: sorted.map((d) => dayNames[d]).join(' / '),
          frequency: sorted.length,
        };
      }
    }

    return originalPlanDaysInfo;
  }, [modalTab, draftDatetimeList, originalPlanDaysInfo]);

  const handleSaveCalendarChanges = async () => {
    try {
      const trainerId =
        (programData as any)?.trainerId || sessionStore.getState().currentUser?.uid || '';
      const programId = (programData as any)?.programId || (programData as any)?.id || '';

      const WEEKDAY_ORDER: Record<string, number> = {
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
        sunday: 7,
      };

      let howOftenTrain: Array<{ slug: string; value: string }> | undefined = undefined;

      if (modalTab === 'permanent') {
        const uniqueDaySlugs = new Set<string>();
        draftDatetimeList.forEach((item: any) => {
          const w = String(item.weekday || '')
            .trim()
            .toLowerCase();
          if (w && WEEKDAY_ORDER[w]) {
            uniqueDaySlugs.add(w);
          }
        });
        const sortedDays = Array.from(uniqueDaySlugs).sort(
          (a, b) => (WEEKDAY_ORDER[a] || 99) - (WEEKDAY_ORDER[b] || 99),
        );
        howOftenTrain = sortedDays.map((d) => ({ slug: d, value: d }));
      }

      await updateProgramSchedule.mutateAsync({
        clientId: clientId,
        trainerId: trainerId,
        programId: programId,
        datetimeList: draftDatetimeList,
        changeType: modalTab,
        howOftenTrain: howOftenTrain,
      });

      // Refresh the dashboard data
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['clientProgramTab'] }),
        queryClient.invalidateQueries({ queryKey: ['clientWorkoutsTab'] }),
        queryClient.invalidateQueries({ queryKey: ['clientOverview'] }),
      ]);

      toast.success('Program schedule updated successfully');
      setModalTab('permanent');
      setIsPlanCalendarModalOpen(false);

      // Reset Plan Calendar tab to index 0 (Week 1)
      setActiveWeek(1);
    } catch {
      setInlineError('Failed to update program schedule');
    }
  };

  const currentModalCalendarDays = generateModalCalendarDays(modalDate);

  const handleModalPrevMonth = () => {
    setModalDate(new Date(modalDate.getFullYear(), modalDate.getMonth() - 1, 1));
  };

  const handleModalNextMonth = () => {
    setModalDate(new Date(modalDate.getFullYear(), modalDate.getMonth() + 1, 1));
  };

  const getWeekDays = (date: Date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);

    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  };

  const currentWeekDays = getWeekDays(currentDate);

  const formatWeekRange = (days: Date[]) => {
    const start = days[0];
    const end = days[6];
    const startMonth = start.toLocaleString('en-US', { month: 'short' });
    const endMonth = end.toLocaleString('en-US', { month: 'short' });

    if (startMonth === endMonth) {
      return `${start.getDate()} - ${end.getDate()} ${startMonth}`;
    }
    return `${start.getDate()} ${startMonth} - ${end.getDate()} ${endMonth}`;
  };

  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  // Sync week calendar view to program date range
  useEffect(() => {
    if (programData?.datetimeList && programData.datetimeList.length > 0) {
      setCurrentDate(new Date());
      setModalDate(new Date());
    }
  }, [programData]);

  useEffect(() => {
    const updateVisibleCount = () => {
      if (thumbnailsContainerRef.current) {
        // Get container width and calculate how many 76px thumbnails + 6px gaps (82px) can fit.
        // We subtract 1 to reserve space for the .more-btn which is always displayed.
        const containerWidth = thumbnailsContainerRef.current.clientWidth;
        const maxItems = Math.max(1, Math.floor(containerWidth / 82) - 1);
        setVisibleThumbnails(maxItems);
      }
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  if (isLoading) {
    return (
      <div
        className="program-tab-content loading"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          gap: 16,
        }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: '#ffa100' }} spin />} />
        <span style={{ fontSize: 16, fontWeight: 500, color: '#ffffffb3' }}>Loading Program</span>
      </div>
    );
  }

  const isDraftProgram = programData?.isDraft === true || (programData as any)?.status === 'draft';

  const generatorType =
    (programData as any)?.generatorType === 'manual' ||
    programData?.isManually === true ||
    (programData as any)?.type === 'manual'
      ? 'manual'
      : 'ai';

  if (
    !programData ||
    (programData as any).status === 'no_program' ||
    !(programData as any).programId ||
    isDraftProgram
  ) {
    const isInactive = client?.is_active === false || client?.isActive === false;
    return (
      <div className="program-tab-content">
        <div
          className="dashboard-empty-state-wrapper"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            width: '100%',
          }}
        >
          <EmptyState
            message={
              isInactive
                ? 'This client is currently inactive. Activate this client to create and assign programs.'
                : isDraftProgram
                  ? 'You have an unfinished program draft for this client.'
                  : 'No program yet.'
            }
            buttonText={
              isInactive
                ? undefined
                : isDraftProgram
                  ? 'Continue to Finish Program'
                  : 'Create Your First Program'
            }
            onButtonClick={
              isInactive
                ? undefined
                : isDraftProgram
                  ? () =>
                      navigate(
                        `/onboarding-trainer/dashboard/program-generator/${generatorType}?programId=${(programData as any)?.programId}`,
                      )
                  : () => navigate('/onboarding-trainer/dashboard')
            }
            hideIcon
            hideMessage={false}
            marginTop="0"
          />
        </div>
      </div>
    );
  }

  const isManuallyGenerated =
    programData?.isManually === true ||
    programData?.isAIGenerate === false ||
    !programData?.progress?.training_goal;

  return (
    <div className="program-tab-content">
      <div className="program-cards-grid">
        {/* Card 1: Intensity & Setup */}
        <div className="program-card">
          <div className="card-header">
            <div className="icon">
              <Intensity_setupIcon />
            </div>
            <h3>Intensity & Setup</h3>
            {!isManuallyGenerated && (
              <button className="edit-btn">
                <EditIcon
                  onClick={() => handleEditProgram('program-intensity')}
                  style={{ cursor: 'pointer' }}
                />
              </button>
            )}
          </div>
          <div className="card-body">
            <div className="info-row align-center space-between">
              <span className="label">
                Intensity: <span className="value">{getSlugTitle(programData.intensity)}</span>
              </span>
              <div className="value-dots">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div
                    key={idx}
                    className={`dot ${idx < getIntensityActiveDots(programData?.intensity) ? 'active' : ''}`}
                  />
                ))}
              </div>
            </div>
            {(() => {
              const equipments = programData.equipments || [];
              const visibleEquipments = equipments.slice(0, 3);
              const remainingCount = equipments.length - 3;

              return (
                <>
                  {visibleEquipments.map((eq: string, i: number) => (
                    <div key={i} className="info-row align-center space-between">
                      <span className="value">{getSlugTitle(eq)}</span>
                      <span className="icon-img">
                        <EquipmentIcon slug={eq} />
                      </span>
                    </div>
                  ))}
                  {equipments.length > 3 && (
                    <div
                      className="show-more-toggle"
                      onClick={() => setIsEquipmentsModalOpen(true)}
                    >
                      Show +{remainingCount}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* Card 2: Program Goals */}
        <div className="program-card has-bg-image">
          <div className="card-header">
            <div className="icon">
              <Program_goalsIcon />
            </div>
            <h3>Program Goals</h3>
            {!isManuallyGenerated && (
              <button className="edit-btn">
                <EditIcon
                  onClick={() => handleEditProgram('training-goal')}
                  style={{ cursor: 'pointer' }}
                />
              </button>
            )}
          </div>
          <div className="card-body">
            <div className="info-group">
              <span className="label">Primary Goal</span>
              <span className="value highlight">{getSlugTitle(programData.primaryGoal)}</span>
            </div>
            <div className="info-group">
              <span className="label">Priority Focus</span>
              <span className="value">
                {programData.priorityFocus.map(getSlugTitle).join(', ') || 'Not specified'}
              </span>
            </div>
          </div>
          <img
            src={isFemaleProfile(client) ? ProgramFemaleImg : ProgramMaleImg}
            className="bg-image"
            alt="Program Goal"
          />
        </div>

        {/* Card 3: Goal Approach */}
        <div className="program-card">
          <div className="card-header">
            <div className="icon">
              <Goal_approachIcon />
            </div>
            <h3>Goal Approach</h3>
            {!isManuallyGenerated && (
              <button className="edit-btn">
                <EditIcon
                  onClick={() =>
                    handleEditProgram('training-goal-details', 'training-goal-approaches')
                  }
                  style={{ cursor: 'pointer' }}
                />
              </button>
            )}
          </div>
          <div className="card-body approach-body">
            <div className="left-col">
              <div className="info-group">
                <span className="label">Approach</span>
                <span className="value">
                  {getSlugTitle(programData.goalApproach) || 'Not specified'}
                </span>
              </div>
              <div className="info-group">
                <span className="label">Method</span>
                <span className="value">
                  {programData.goalMethod.map(getSlugTitle).join(' / ') || 'Not specified'}
                </span>
              </div>
            </div>
            <div className="right-col chart-bars">
              {[60, 40, 60, 30, 80, 50, 70, 90, 60, 40].map((h, i) => (
                <div key={i} className={`bar bar-h${h}`} />
              ))}
            </div>
          </div>
        </div>

        {/* Card 4: Program Status */}
        <div className="program-card program-status">
          <div className="card-header">
            <div className="icon">
              <Plan_structureIcon />
            </div>
            <h3>Program Status</h3>
            <button className="edit-btn">
              <EditIcon
                onClick={() => handleEditProgram('program-duration')}
                style={{ cursor: 'pointer' }}
              />
            </button>
          </div>
          <div className="card-body ">
            <div className="info-row align-center status-grid">
              <span className="label">Time</span>
              <span className="value">{programData.timeMin} min</span>
            </div>
            <div className="info-row align-center status-grid">
              <span className="label">Week</span>
              <span className="value">
                {String(programData.currentWeek).toLowerCase().includes('not started')
                  ? `Not started (${programData.totalWeeks || ''})`.replace('()', '').trim()
                  : `${programData.currentWeek} of ${programData.totalWeeks}`}
              </span>
            </div>
            <div className="info-row align-center status-grid">
              <span className="label">Frequency</span>
              <span className="value">{programData.frequency}</span>
            </div>
            <div className="info-row align-center status-grid">
              <span className="label">Status</span>
              <span className={`status-badge ${(programData.status || 'active').toLowerCase()}`}>
                {(programData.status || 'active').toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="middle-cards-grid">
        <div className="program-card today-workout-card">
          <div className="card-header">
            <div className="icon">
              <ProgramWorkoutIcon />
            </div>
            <h3>
              <span className="main-title">{client?.name || 'Client'}&apos;s today workout </span>
              <span className="subtitle">
                &bull; {programData.todayWorkout.day}, {programData.todayWorkout.date}
              </span>
            </h3>
            <button className="edit-btn">
              <EditIcon
                onClick={() =>
                  navigate(
                    `/onboarding-trainer/dashboard/program-generated/${programData.programId}`,
                    {
                      state: {
                        clientId,
                        clientName,
                        isManually: isManuallyGenerated,
                        generatorType: isManuallyGenerated ? 'manual' : 'ai',
                      },
                    },
                  )
                }
                style={{ cursor: 'pointer' }}
              />
            </button>
          </div>
          <div className="card-body">
            <div className="week-selector">
              <span
                className={`nav-arrow left ${isFirstWeek ? 'disabled' : ''}`}
                onClick={handlePrevWeekTab}
                style={
                  isFirstWeek
                    ? { opacity: 0.25, cursor: 'not-allowed', pointerEvents: 'none' }
                    : { cursor: 'pointer' }
                }
              >
                <NavArrowIcon />
              </span>
              <div className="weeks-list" ref={weeksListRef}>
                {weeksData.map((weekObj: any) => (
                  <span
                    key={weekObj.week}
                    className={activeWeek === weekObj.week ? 'active' : ''}
                    onClick={() => setActiveWeek(weekObj.week)}
                  >
                    Week {weekObj.week}
                  </span>
                ))}
              </div>
              <span
                className={`nav-arrow right ${isLastWeek ? 'disabled' : ''}`}
                onClick={handleNextWeekTab}
                style={
                  isLastWeek
                    ? { opacity: 0.25, cursor: 'not-allowed', pointerEvents: 'none' }
                    : { cursor: 'pointer' }
                }
              >
                <NavArrowIcon />
              </span>
            </div>
            <div className="day-selector">
              {activeWeekData.days.map((dayObj: any, idx: number) => (
                <div
                  key={idx}
                  className={`day-circle ${activeDayIndex === idx ? 'active' : ''}`}
                  onClick={() => setActiveDayIndex(idx)}
                >
                  {dayObj.dayLabel}
                </div>
              ))}
            </div>
            <div className="thumbnails-row" ref={thumbnailsContainerRef}>
              {exercisesToRender.slice(0, visibleThumbnails).map((item: any, idx: number) => {
                const isMale = isMaleProfile(client);
                const imgUrl = isMale
                  ? item.image_male_url || item.image_female_url
                  : item.image_female_url || item.image_male_url;
                return (
                  <div key={idx} className="thumbnail">
                    {imgUrl ? (
                      <img src={imgUrl} alt={item.en_display_name || item.name} />
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          backgroundColor: '#2a2d3d',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '8px',
                          color: '#8a8d9d',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                        }}
                      >
                        {(item.en_display_name || item.name || 'EX').substring(0, 2)}
                      </div>
                    )}
                  </div>
                );
              })}
              <div
                className="thumbnail more-btn cursor-pointer"
                onClick={() => {
                  const targetUid = clientId || client?.uid || client?.id;
                  if (targetUid) {
                    navigate(
                      `/onboarding-trainer/dashboard/clients/${targetUid}/workouts/${getSelectedWorkoutId()}`,
                    );
                  }
                }}
              >
                <MoreProgramWorkoutIcon />
              </div>
            </div>
          </div>
        </div>

        <div className="program-card plan-calendar-card">
          <div className="card-header">
            <div className="icon">
              <PlanCalendarIcon />
            </div>
            <h3>Plan Calendar</h3>
            <button className="edit-btn" onClick={() => setIsPlanCalendarModalOpen(true)}>
              <EditIcon />
            </button>
          </div>
          <div className="card-body">
            <div className="schedule-info">
              <div className="schedule-item">
                <CalendarIcon className="small-icon" />
                <span>{originalPlanDaysInfo.daysString}</span>
              </div>
              <div className="schedule-item evening-reminder">
                <NotificationIcon className="small-icon" />
                <span>{programData?.planCalendar?.reminderTime || 'Evening reminder'}</span>
              </div>
            </div>
            <div className="calendar-box">
              <div className="week-range">
                <span>This week</span>
                <div className="range-selector">
                  <span className="nav-arrow left" onClick={handlePrevWeek}>
                    <NavArrowIcon />
                  </span>
                  <span>{formatWeekRange(currentWeekDays)}</span>
                  <span className="nav-arrow right" onClick={handleNextWeek}>
                    <NavArrowIcon />
                  </span>
                </div>
              </div>
              <div className="calendar-days">
                {currentWeekDays.map((date, index) => {
                  const dayName = date
                    .toLocaleString('en-US', { weekday: 'short' })
                    .substring(0, 2);

                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const ymd = `${year}-${month}-${day}`;

                  const today = new Date();
                  const todayYear = today.getFullYear();
                  const todayMonth = String(today.getMonth() + 1).padStart(2, '0');
                  const todayDay = String(today.getDate()).padStart(2, '0');
                  const todayYmd = `${todayYear}-${todayMonth}-${todayDay}`;

                  const isToday = ymd === todayYmd;
                  const isPast = ymd < todayYmd;
                  const isFuture = ymd > todayYmd;

                  const datetimeList = programData?.datetimeList || [];
                  const isWorkoutScheduled = datetimeList.some((item: any) => item.ymd === ymd);

                  const completedWorkouts = programData?.completedWorkoutsYmds || [];
                  const isWorkoutDone = completedWorkouts.includes(ymd);

                  let stateClass = '';
                  let showDumbbell = false;

                  if (isPast) {
                    if (isWorkoutDone) {
                      stateClass = 'past-done';
                      showDumbbell = true;
                    } else if (isWorkoutScheduled) {
                      stateClass = 'past-workout';
                      showDumbbell = true;
                    } else {
                      stateClass = 'past-no-workout';
                      showDumbbell = false;
                    }
                  } else if (isToday) {
                    if (isWorkoutScheduled || isWorkoutDone) {
                      stateClass = 'today-workout';
                      showDumbbell = true;
                    } else {
                      stateClass = 'today-no-workout';
                      showDumbbell = false;
                    }
                  } else if (isFuture) {
                    if (isWorkoutScheduled) {
                      stateClass = 'future-workout';
                      showDumbbell = true;
                    } else {
                      stateClass = 'future-no-workout';
                      showDumbbell = false;
                    }
                  }

                  return (
                    <div className="cal-day" key={index}>
                      <span className="day-name">{dayName}</span>
                      <div className={`day-num ${stateClass}`}>
                        <span>{date.getDate()}</span>
                        {showDumbbell && <ProgramWeightIcon className="cal-icon" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="performance-adherence-section">
        <div className="performance-chart-card">
          <div className="card-header space-between">
            <div className="title-left">
              <div className="icon">
                <PerformanceAdherenceIcon />
              </div>
              <h3>Workout Performance & Adherence</h3>
            </div>
            <div className="chart-nav-arrows">
              <span
                className={`nav-arrow left ${!chartScrollInfo.canScrollLeft ? 'disabled' : ''}`}
                onClick={() => handleScrollChart('left')}
                title="Scroll Left"
              >
                <NavArrowIcon />
              </span>
              <span
                className={`nav-arrow right ${!chartScrollInfo.canScrollRight ? 'disabled' : ''}`}
                onClick={() => handleScrollChart('right')}
                title="Scroll Right"
              >
                <NavArrowIcon />
              </span>
            </div>
          </div>

          <div className="chart-container">
            <div className="y-axis">
              <span className="y-label">Time (min)</span>
              <span>60</span>
              <span>45</span>
              <span>30</span>
              <span>15</span>
              <span>0</span>
            </div>
            <div
              className="chart-content"
              ref={chartContentRef}
              onScroll={updateChartScroll}
              onMouseDown={handleChartMouseDown}
              onMouseMove={handleChartMouseMove}
              onMouseUp={handleChartMouseUpOrLeave}
              onMouseLeave={handleChartMouseUpOrLeave}
            >
              <div className="bars-wrapper">
                {((programData as any)?.datetimeList || []).map((item: any, index: number) => {
                  const ymd = item.ymd;
                  if (!ymd) return null;

                  const [y, m, d] = ymd.split('-');
                  const dateObj = new Date(Number(y), Number(m) - 1, Number(d));
                  const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });

                  const completedWorkouts = (programData as any)?.completedWorkoutsYmds || [];
                  const isWorkoutDone = completedWorkouts.includes(ymd);

                  const todayYmd = getLocalYMD(new Date());
                  const isPast = ymd < todayYmd;

                  const planned = (programData as any)?.timeMin || 45;

                  let actual = 0;
                  if (isWorkoutDone) {
                    actual = planned - (index % 6); // Mock completed duration
                  } else if (isPast) {
                    actual = 0; // Missed workout
                  } else if (ymd === todayYmd) {
                    // Maybe done later today, keep 0
                  }

                  const plannedHeight = (planned / 60) * 100;
                  const actualHeight = (actual / 60) * 100;

                  return (
                    <div className="day-group" key={index}>
                      <div className="bar-pair">
                        <div
                          className="bar planned"
                          style={{ height: `${Math.min(100, plannedHeight)}%` }}
                        >
                          <span className="bar-value">{planned}</span>
                        </div>
                        {(actual > 0 || isPast) && (
                          <div
                            className="bar actual"
                            style={{ height: `${Math.min(100, actualHeight)}%` }}
                          >
                            <span className="bar-value">{actual}</span>
                          </div>
                        )}
                      </div>
                      <span className="day-label">{dayName}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="chart-legend">
            <div className="legend-item">
              <span className="dot planned"></span>
              <span>Planned Time (min)</span>
            </div>
            <div className="legend-item">
              <span className="dot actual"></span>
              <span>Actual Time (min)</span>
            </div>
          </div>
        </div>

        <div className="performance-metrics-card">
          <div className="metric-item">
            <span className="metric-label">WORKOUT COMPLETION</span>
            <span className="metric-value">
              {programData?.adherence?.workoutCompletion || '0%'}
            </span>
            {(() => {
              const val = programData?.adherence?.workoutCompletionNum || 0;
              let badgeColor = 'red';
              let badgeText = 'Low';
              if (val >= 75) {
                badgeColor = 'green';
                badgeText = 'Good';
              } else if (val >= 40) {
                badgeColor = 'yellow';
                badgeText = 'Average';
              }
              return <span className={`status-badge ${badgeColor}`}>{badgeText}</span>;
            })()}
          </div>
          <div className="metric-divider" />
          <div className="metric-item">
            <span className="metric-label">AVG. TIME ADHERENCE</span>
            <span className="metric-value">
              {programData?.adherence?.avgTimeAdherence || '0/0 min'}
            </span>
            {(() => {
              const val = programData?.adherence?.avgTimeAdherenceNum || 0;
              const target = programData?.timeMin || 45;
              const pct = target > 0 ? (val / target) * 100 : 0;
              let badgeColor = 'red';
              let badgeText = 'Off Track';
              if (pct >= 75) {
                badgeColor = 'green';
                badgeText = 'On Track';
              } else if (pct >= 40) {
                badgeColor = 'yellow';
                badgeText = 'Average';
              }
              return <span className={`status-badge ${badgeColor}`}>{badgeText}</span>;
            })()}
          </div>
          <div className="metric-divider" />
          <div className="metric-item">
            <span className="metric-label">PLAN RESPECT / CONSISTENCY</span>
            <span className="metric-value">
              {programData?.adherence?.planRespect || '0 of 0 workouts'}
            </span>
            {(() => {
              const val = programData?.adherence?.planRespectNum || 0;
              const total = programData?.adherence?.planRespectTotal || 0;
              const pct = total > 0 ? (val / total) * 100 : 0;
              let badgeColor = 'red';
              let badgeText = 'Off Track';
              if (pct >= 75) {
                badgeColor = 'green';
                badgeText = 'On Track';
              } else if (pct >= 40) {
                badgeColor = 'yellow';
                badgeText = 'Average';
              }
              return <span className={`status-badge ${badgeColor}`}>{badgeText}</span>;
            })()}
          </div>
        </div>
      </div>

      <Modal
        open={isPlanCalendarModalOpen}
        footer={null}
        onCancel={() => setIsPlanCalendarModalOpen(false)}
        centered
        width={520}
        closeIcon={null}
        rootClassName="plan-calendar-modal"
      >
        <div className="plan-calendar-modal-content">
          <div className="modal-header">
            <h3>Plan Calendar</h3>
          </div>

          <div className="modal-subtitle">
            <div className="subtitle-item">
              <CalendarIcon /> {modalPlanDaysInfo.daysString}
            </div>
            <div className="subtitle-item">
              <CalendarIcon /> {modalPlanDaysInfo.frequency} days a week
            </div>
          </div>

          <div className="modal-tabs">
            <button
              className={`tab ${modalTab === 'permanent' ? 'active' : ''}`}
              onClick={() => setModalTab('permanent')}
            >
              Permanent Change
            </button>
            <button
              className={`tab ${modalTab === 'temporary' ? 'active' : ''}`}
              onClick={() => setModalTab('temporary')}
            >
              Temporary Change
            </button>
          </div>

          <div className="info-banner">
            <OnboardModalInfoIcon className="info-icon" />
            <span>
              {modalTab === 'permanent'
                ? 'Tapping a day updates the recurring weekly schedule for all future weeks'
                : 'Tapping a day overrides only that specific date without changing the weekly pattern'}
            </span>
          </div>

          {inlineError && (
            <div
              className="inline-error"
              style={{
                color: '#ff4d4f',
                marginTop: '12px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {inlineError}
            </div>
          )}

          <div className="calendar-widget">
            <div className="calendar-header">
              <span>{modalDate.toLocaleString('en-US', { month: 'long', year: 'numeric' })}</span>
              <div className="nav-arrows">
                <span className="nav-arrow left" onClick={handleModalPrevMonth}>
                  <NavArrowIcon />
                </span>
                <span className="nav-arrow right" onClick={handleModalNextMonth}>
                  <NavArrowIcon />
                </span>
              </div>
            </div>

            <div className="calendar-grid">
              <div className="weekdays">
                <span>Mo</span>
                <span>Tu</span>
                <span>We</span>
                <span>Th</span>
                <span>Fr</span>
                <span>Sa</span>
                <span>Su</span>
              </div>
              <div className="days">
                {currentModalCalendarDays.map((day, idx) => (
                  <div
                    key={idx}
                    className={`day ${day.isCurrentMonth ? '' : 'inactive'} ${day.isPast ? 'past-day' : ''}`}
                    onClick={() => handleDayClick(day)}
                    style={{
                      cursor: day.isPast ? 'not-allowed' : 'pointer',
                      opacity: day.isPast ? 0.5 : 1,
                    }}
                  >
                    <div
                      className={`date-circle ${day.isWorkout ? 'workout' : ''} ${day.isToday ? 'green-glow' : ''} ${day.isSelectedSource ? 'selected-source' : ''}`}
                    >
                      <span className="date-num">{day.date}</span>
                      {day.isWorkout && <ProgramWeightIcon className="dumbbell-icon" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="calendar-legend">
            <div className="legend-item">
              <span className="dot active"></span> Active workout
            </div>
            <div className="legend-item">
              <span className="dot filled"></span>
              {/* Scheduled workout */}
              Skipped / changed
            </div>
            <div className="legend-item">
              <span className="dot outline"></span> Regular day
            </div>
          </div>

          <div className="modal-actions">
            <button
              className="btn-cancel"
              onClick={() => setIsPlanCalendarModalOpen(false)}
              disabled={updateProgramSchedule.isPending}
            >
              Cancel
            </button>
            <button
              className="btn-save"
              onClick={handleSaveCalendarChanges}
              disabled={updateProgramSchedule.isPending}
            >
              {updateProgramSchedule.isPending ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    color: '#050621',
                  }}
                >
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 16, color: '#050621' }} spin />}
                  />
                  <span>Saving...</span>
                </div>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </Modal>

      <EquipmentsModal
        isOpen={isEquipmentsModalOpen}
        onClose={() => setIsEquipmentsModalOpen(false)}
        equipments={programData?.equipments || []}
        getSlugTitle={getSlugTitle}
      />
    </div>
  );
};
